import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormup } from "@formup/core";
import classNames from "classnames";
import nextDynamic from "next/dynamic";
import Link from "next/link";
import { useMutation } from "react-apollo";
import { Cookies } from "react-cookie";

import useSession from "dynamic/hooks/useSession";
import { useLayoutWrapper } from "dynamic/contexts/layoutWrapper/LayoutWrapperContext";
import yup from "dynamic/settings/yup";
import { PROFILE_PAGE_PATH, REQUEST_RESET_PASSWORD_PAGE_PATH } from "dynamic/routes";
import handleOnKeyPress from "utils/handleOnKeyPress";
import autoComplete from "constants/autoComplete";
import { useModal } from "dynamic/contexts/modal/ModalContext";
import InvalidCustomerTypeLoginModal from "dynamic/components/ModalComponents/InvalidCustomerTypeLoginModal";
import { checkIsCustomerTypeInvalid, getCustomerTypeCheckoutPath } from "constants/customerTypes";
import { useNavigation, useNavigationRouteState } from "contexts/navigation/NavigationContext";
import InvalidCustomerTypeModal from "dynamic/components/ModalComponents/InvalidCustomerTypeModal";
import { onQueryError } from "utils/queryHandlers";
import { useSnackbar } from "dynamic/contexts/snackbar/SnackbarContext";
import dynamic from "dynamic/hocs/Dynamic";
import CheckoutLeadCard from "dynamic/components/CheckoutProcess/CheckoutLeadCard";
import LOGIN_MUTATION from "graphql/loginMutation";
import Input from "dynamic/components/FormComponents/Input";
import { CUSTOMER_TYPE_COOKIE_KEY } from "constants/cookies";

const InputErrorMessage = nextDynamic(() => import("dynamic/components/FormComponents/InputErrorMessage"));
const AlertCard = nextDynamic(() => import("dynamic/components/AlertCard"));

const schema = yup.object().shape({
  email: yup
    .string()
    .email()
    .required(),
  password: yup
    .string()
    .required(),
});

const Login = () => {
  const {
    email: initialEmail,
    selectedProduct,
    updatedPassword,
    redirectTo,
  } = useNavigationRouteState();

  const [navigate] = useNavigation();

  const [t] = useTranslation();
  const [login, { loading: loadingSignIn }] = useMutation(LOGIN_MUTATION);
  const [mutationError, setMutationError] = useState(false);
  const [showModal] = useModal();
  const notify = useSnackbar();
  const { setAuthenticatedSession, endSession } = useSession();

  const { setDesktopCard } = useLayoutWrapper();

  useEffect(() => {
    if (selectedProduct?.id) {
      setDesktopCard({
        component: CheckoutLeadCard,
        componentProps: {
          showLeasingInformation: false,
        },
      });
    }
  }, [selectedProduct, setDesktopCard]);

  const handleRedirectAfterLogin = useCallback((user) => {
    if (!user?.customer) {
      showModal({
        isCloseable: true,
        isCloseableOnOverlay: true,
        component: InvalidCustomerTypeLoginModal,
        onClose: () => endSession(),
      });

      return;
    }

    const customerType = user.customer?.type;

    const isNormalLogin = (
      !selectedProduct?.id
      && !redirectTo
    );

    if (isNormalLogin) {
      navigate(PROFILE_PAGE_PATH, { customerType, updatedPassword: false });
      return;
    }

    const isInvalidCustomerType = checkIsCustomerTypeInvalid({
      selectedProduct,
      customerType,
    });

    if (isInvalidCustomerType) {
      showModal({
        isCloseable: true,
        isCloseableOnOverlay: true,
        component: InvalidCustomerTypeModal,
        componentProps: {
          customerType,
        },
      });

      return;
    }

    const path = redirectTo ?? getCustomerTypeCheckoutPath({
      offerUrl: selectedProduct?.offerUrl,
      customerType,
    });

    navigate(path, {
      customerType,
      updatedPassword: false,
    });
  }, [
    selectedProduct,
    endSession,
    redirectTo,
    showModal,
    navigate,
  ]);

  const handleSubmitForm = useCallback((values) => {
    const { email, password } = values;

    login({
      variables: {
        email,
        password,
      },
    })
      .then((response) => {
        setMutationError(false);
        setAuthenticatedSession();

        if (!response?.data) {
          return;
        }

        const { user } = response.data.signIn;

        new Cookies().set(
          CUSTOMER_TYPE_COOKIE_KEY,
          user?.customer?.type,
        );

        handleRedirectAfterLogin(user);
      })
      .catch((error) => {
        onQueryError({
          notify,
          error,
        });

        setMutationError(true);
      });
  }, [
    handleRedirectAfterLogin,
    setAuthenticatedSession,
    notify,
    login,
  ]);

  const {
    formikForm,
    submitForm,
    FormInput,
    Form,
  } = useFormup(schema, {
    onSubmit: handleSubmitForm,
    initialValues: {
      email: initialEmail,
    },
  });

  const buttonClasses = classNames("btn btn--primary btn--large max-w-full my-4 flex justify-center", {
    "btn--disabled": loadingSignIn,
  });

  return (
    <>
      <div className="container p-5 max-w-md m-auto flex flex-col">

        {
          updatedPassword && (
            <AlertCard
              message="Ihr Passwort wurde erfolgreich geändert"
              color="secondary"
              className="max-w-full mb-5"
            />
          )
        }

        <h1 className="text-lg leading-tight text-black mb-4">
          {t("authentication.please_enter_your_credentials")}
        </h1>

        <Form formikForm={formikForm}>
          <div className="pb-2 lg:mr-2 lg:flex-grow">
            <FormInput
              type="email"
              name="email"
              placeholder={t("authentication.email_address")}
              aria-label={t("authentication.email_address")}
              label={t("authentication.email_address")}
              component={Input}
              injectFormupData
              autoComplete={autoComplete.EMAIL}
            />
          </div>

          <div className="pt-5 pb-2 lg:mr-2 lg:flex-grow">
            <FormInput
              type="password"
              name="password"
              placeholder={t("authentication.password")}
              aria-label={t("authentication.password")}
              label={t("authentication.password")}
              component={Input}
              injectFormupData
              onKeyPress={handleOnKeyPress(submitForm)}
              autoComplete={autoComplete.PASSWORD}
            />
          </div>

          {
            mutationError && (
              <InputErrorMessage className="mb-2">
                {t("errors.an_error_occurred_during_login")}
              </InputErrorMessage>
            )
          }

          <button
            type="submit"
            onClick={submitForm}
            disabled={loadingSignIn}
            className={buttonClasses}
          >
            {t(loadingSignIn ? "actions.loading" : "buttons.confirm")}
          </button>
        </Form>
        <div className="ml-auto">
          <Link href={REQUEST_RESET_PASSWORD_PAGE_PATH}><a className="link link--secondary" href={REQUEST_RESET_PASSWORD_PAGE_PATH}>Passwort vergessen?</a></Link>
        </div>
      </div>
    </>
  );
};

export default dynamic(Login);
